import {
  ChronoUnit,
  Instant,
  LocalDate,
  LocalTime,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import { CompanyGigShift, PaymentMethod } from "model/Gig";
import type { Worker } from "../../../organizationalStructure/chooseWorkersModal.types";

export type CompanySetting = {
  key: string;
  value?: string | number | null;
};

/** Safely parses an integer value and falls back when parsing fails. */
export const safeParseInt = (val: string | undefined, fallback = 0): number => {
  const n = Number.parseInt(val ?? "", 10);
  return Number.isFinite(n) ? n : fallback;
};

/** Parses any numeric-like input to a non-negative finite number. */
export const parseNonNegativeNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

/** Keeps digits only and trims to the provided max length. */
export const sanitizeNumericInput = (
  value: string,
  maxDigits: number,
): string => value.replace(/[^0-9]/g, "").slice(0, maxDigits);

/** Parses a zoned date-time string and converts it to the provided zone. */
export const parseZonedDateTime = (
  timeStr: string,
  zone: ZoneId,
): ZonedDateTime | null => {
  try {
    return ZonedDateTime.parse(timeStr).withZoneSameInstant(zone);
  } catch {
    return null;
  }
};

/** Parses an instant string and converts it to a zoned date-time. */
export const parseInstantToZonedDateTime = (
  timeStr: string,
  zone: ZoneId,
): ZonedDateTime | null => {
  try {
    const instant = Instant.parse(timeStr);
    return ZonedDateTime.ofInstant(instant, zone);
  } catch {
    return null;
  }
};

/** Parses supported time formats into a local time for the given zone. */
export const parseToLocalTime = (timeStr: string, zone: ZoneId): LocalTime => {
  if (!timeStr) throw new Error("Invalid time");

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
    return LocalTime.parse(timeStr);
  }

  const zonedDateTime = parseZonedDateTime(timeStr, zone);
  if (zonedDateTime) return zonedDateTime.toLocalTime();

  const instantZonedDateTime = parseInstantToZonedDateTime(timeStr, zone);
  if (instantZonedDateTime) return instantZonedDateTime.toLocalTime();

  throw new Error(`Unsupported time format: ${timeStr}`);
};

/** Creates a zoned date-time from a local date and time string. */
export const createZonedDateTime = (
  date: LocalDate,
  timeStr: string,
  zone: ZoneId,
): ZonedDateTime => date.atTime(parseToLocalTime(timeStr, zone)).atZone(zone);

/** Builds shift start/end boundaries and handles overnight shifts. */
export const createShiftBoundary = (
  date: LocalDate,
  startTime: string,
  endTime: string,
  zone: ZoneId,
  endsSameDay: (start: string, end: string) => boolean,
): { start: ZonedDateTime; end: ZonedDateTime } => {
  const endDay = endsSameDay(startTime, endTime) ? date : date.plusDays(1);
  return {
    start: createZonedDateTime(date, startTime, zone),
    end: createZonedDateTime(endDay, endTime, zone),
  };
};

/** Parses a zone id and falls back to Europe/Stockholm if invalid. */
export const parseZoneId = (zoneIdValue: string): ZoneId => {
  try {
    return ZoneId.of(zoneIdValue);
  } catch {
    return ZoneId.of("Europe/Stockholm");
  }
};

/** Returns non-negative whole minutes between two zoned date-times. */
export const minutesBetween = (
  start: ZonedDateTime,
  end: ZonedDateTime,
): number => Math.max(0, Math.floor(start.until(end, ChronoUnit.MINUTES)));

/** Returns a company setting value or a fallback when missing. */
export const getCompanySetting = (
  settings: CompanySetting[] | undefined,
  key: string,
  fallback: string | number,
): string | number =>
  settings?.find((setting) => setting.key === key)?.value ?? fallback;

/** Returns a company setting as a string with fallback support. */
export const getCompanySettingString = (
  settings: CompanySetting[] | undefined,
  key: string,
  fallback: string,
): string => String(getCompanySetting(settings, key, fallback) || fallback);

/** Returns a company setting as a number with fallback support. */
export const getCompanySettingNumber = (
  settings: CompanySetting[] | undefined,
  key: string,
  fallback: number,
): number => Number(getCompanySetting(settings, key, fallback)) || fallback;

/** Checks whether two shifts overlap in time. */
export const shiftsOverlap = (
  a: CompanyGigShift,
  b: CompanyGigShift,
): boolean => {
  if (!a.startTime || !a.endTime || !b.startTime || !b.endTime) return false;

  const aStart = Instant.parse(a.startTime);
  const aEnd = Instant.parse(a.endTime);
  const bStart = Instant.parse(b.startTime);
  const bEnd = Instant.parse(b.endTime);

  return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
};

/** Computes the earliest selectable shift date based on worker payment rules. */
export const computeEarliestSelectableDate = (
  zoneId: ZoneId,
  workers: Worker[],
): LocalDate => {
  const now = ZonedDateTime.now(zoneId).truncatedTo(ChronoUnit.DAYS);

  const anySpay = workers.some(
    (worker) =>
      worker.employmentInfo?.current?.paymentType ===
      PaymentMethod.SIMFLEXITY_PAY,
  );
  const anyOwn = workers.some((worker) => {
    const paymentType = worker.employmentInfo?.current?.paymentType;
    return paymentType != null && paymentType !== PaymentMethod.SIMFLEXITY_PAY;
  });

  const cutoffDay = !workers.length
    ? 4
    : anySpay && anyOwn
      ? 4
      : anySpay
        ? 4
        : 19;

  const lockFrom = now
    .withDayOfMonth(cutoffDay + 1)
    .truncatedTo(ChronoUnit.DAYS);

  const minZdt = (
    now.isBefore(lockFrom)
      ? now.minusMonths(1).withDayOfMonth(1)
      : now.withDayOfMonth(1)
  ).truncatedTo(ChronoUnit.DAYS);

  return minZdt.toLocalDate();
};

/** Returns whether the end time is on the same day as the start time. */
export const isEndSameDay = (
  startTime: string,
  endTime: string,
  zoneId: ZoneId,
): boolean => {
  try {
    const startLocalTime = parseToLocalTime(startTime, zoneId);
    const endLocalTime = parseToLocalTime(endTime, zoneId);
    return !endLocalTime.isBefore(startLocalTime);
  } catch {
    return true;
  }
};

/** Resolves break end date and handles overnight break intervals. */
export const getBreakEndDate = (
  baseDate: LocalDate,
  breakStartTime: string,
  breakEndTime: string,
  zoneId: ZoneId,
): LocalDate =>
  isEndSameDay(breakStartTime, breakEndTime, zoneId)
    ? baseDate
    : baseDate.plusDays(1);

/** Calculates break duration in minutes for a date and optional interval. */
export const calculateBreakMinutesForDate = ({
  date,
  breakStartTime,
  breakEndTime,
  zoneId,
}: {
  date: LocalDate;
  breakStartTime?: string;
  breakEndTime?: string;
  zoneId: ZoneId;
}): number => {
  if (!breakStartTime || !breakEndTime) return 0;

  const breakStartZdt = createZonedDateTime(date, breakStartTime, zoneId);
  const breakEndDay = getBreakEndDate(
    date,
    breakStartTime,
    breakEndTime,
    zoneId,
  );
  const breakEndZdt = createZonedDateTime(breakEndDay, breakEndTime, zoneId);

  return minutesBetween(breakStartZdt, breakEndZdt);
};

/** Validates that break interval is non-negative and within shift boundaries. */
export const isBreakWithinShiftValid = ({
  startTime,
  endTime,
  breakStartTime,
  breakEndTime,
  breakTimeMin,
  showTimeInterval,
  baseDate,
  zoneId,
}: {
  startTime: string;
  endTime: string;
  breakStartTime?: string;
  breakEndTime?: string;
  breakTimeMin?: number;
  showTimeInterval: boolean;
  baseDate: LocalDate;
  zoneId: ZoneId;
}): boolean => {
  if (!showTimeInterval) return (breakTimeMin ?? 0) >= 0;
  if (!breakStartTime || !breakEndTime) return true;
  if (breakStartTime === breakEndTime) return (breakTimeMin ?? 0) >= 0;

  const shiftBoundary = createShiftBoundary(
    baseDate,
    startTime,
    endTime,
    zoneId,
    (start: string, end: string) => isEndSameDay(start, end, zoneId),
  );

  const breakEndDay = getBreakEndDate(
    baseDate,
    breakStartTime,
    breakEndTime,
    zoneId,
  );
  const breakStartZdt = createZonedDateTime(baseDate, breakStartTime, zoneId);
  const breakEndZdt = createZonedDateTime(breakEndDay, breakEndTime, zoneId);

  if (!breakEndZdt.isAfter(breakStartZdt)) return false;
  if (breakStartZdt.isBefore(shiftBoundary.start)) return false;
  if (breakEndZdt.isAfter(shiftBoundary.end)) return false;
  if ((breakTimeMin ?? 0) < 0) return false;

  return true;
};

/** Creates shift payloads for dates based on form values and company zone. */
export const buildShiftsFromDates = ({
  dates,
  startTime,
  endTime,
  breakStartTime,
  breakEndTime,
  breakTimeMin,
  deductionAmount,
  zoneId,
}: {
  dates: LocalDate[];
  startTime: string;
  endTime: string;
  breakStartTime?: string;
  breakEndTime?: string;
  breakTimeMin?: number;
  deductionAmount: number;
  zoneId: ZoneId;
}): CompanyGigShift[] =>
  dates.map((date) => {
    const shiftBoundary = createShiftBoundary(
      date,
      startTime,
      endTime,
      zoneId,
      (start: string, end: string) => isEndSameDay(start, end, zoneId),
    );

    const shift: CompanyGigShift = {
      startTime: shiftBoundary.start.toInstant().toString(),
      endTime: shiftBoundary.end.toInstant().toString(),
      deductionAmount,
    };

    if (breakStartTime && breakEndTime && breakStartTime !== breakEndTime) {
      const breakEndDay = getBreakEndDate(
        date,
        breakStartTime,
        breakEndTime,
        zoneId,
      );
      const breakStartZdt = createZonedDateTime(date, breakStartTime, zoneId);
      const breakEndZdt = createZonedDateTime(
        breakEndDay,
        breakEndTime,
        zoneId,
      );

      shift.breakStartTime = breakStartZdt.toInstant().toString();
      shift.breakEndTime = breakEndZdt.toInstant().toString();
    }

    if (breakTimeMin && breakTimeMin > 0) {
      shift.breakTime = breakTimeMin;
    }

    return shift;
  });
